import { useQuery } from "@apollo/client";
import {
  Box,
  Group,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import {
  IconBuildingCommunity,
  IconBuildingStore,
  IconSum,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { COMMISSION_SUMMARY_REPORT } from "apollo/queries"; // Adjust path accordingly
import SupplierFilter from "pages/supplier/supplierFilter";
import { useState } from "react";
import { formatLocalDate, formatNumber } from "utiles/url";
import DriverFilter from "./driver";

const CommissionStats = () => {
  const [driver, setDriver] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  console.log("supplier", driver);
  const { data, loading, error } = useQuery(COMMISSION_SUMMARY_REPORT, {
    variables: {
      period: timeRange || undefined,
      startDate: selectedStartDate
        ? formatLocalDate(selectedStartDate)
        : undefined,
      endDate: selectedEndDate ? formatLocalDate(selectedEndDate) : undefined,
      supplier_id: supplier ? supplier : null,
      driver_id: driver ? driver.value : null,
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to get Drivers Transactions ",
      });
    },
  });

  if (loading) return <StatsSkeleton />;
  if (error) return <Text color="red">Error loading summary</Text>;

  const report = data?.getCommissionSummaryReport || {};
  const {
    total_supplier_commission = 0,
    total_sme_commission = 0,
    total_driver_commission = 0,
    total_commission = 0,
  } = report;

  const supplierPct =
    total_commission > 0
      ? (total_supplier_commission / total_commission) * 100
      : 0;
  const driverPct =
    total_commission > 0
      ? (total_driver_commission / total_commission) * 100
      : 0;

  const renderFilters = () => (
    <Box style={{ position: "relative", zIndex: 10000 }}>
      <Group
        justify="space-between"
        align="flex-start"
        style={{
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <DriverFilter user={true} driver={driver} onCardClick={setDriver} />

        <SupplierFilter supplier={supplier} onCardClick={setSupplier} />
        <Select
          style={{ minWidth: "140px" }}
          data={[
            { value: "DAILY", label: "Daily" },
            { value: "WEEKLY", label: "Weekly" },
            { value: "MONTHLY", label: "Monthly" },
            { value: "QUARTERLY", label: "Half Year" },
            { value: "YEARLY", label: "Yearly" },
          ]}
          value={timeRange}
          onChange={(value) => setTimeRange(value)}
          placeholder="Period"
          withinPortal
          clearable
        />
        <DatePicker
          style={{ minWidth: "140px" }}
          value={selectedStartDate}
          onChange={setSelectedStartDate}
          placeholder="Start Date"
          popoverProps={{ withinPortal: true, zIndex: 10001 }}
          clearable
        />
        <DatePicker
          style={{ minWidth: "140px" }}
          value={selectedEndDate}
          onChange={setSelectedEndDate}
          placeholder="End Date"
          popoverProps={{ withinPortal: true, zIndex: 10001 }}
          clearable
        />
      </Group>
    </Box>
  );
  return (
    <div>
      {renderFilters()}

      <Stack spacing="md" mt="md" mb="xl">
        <SimpleGrid cols={4} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          <StatsCard
            title="Supplier Commission"
            value={total_supplier_commission}
            icon={<IconBuildingStore size={24} />}
            color="blue"
          />

          <StatsCard
            title="SME Commission"
            value={total_sme_commission}
            icon={<IconBuildingCommunity size={24} />}
            color="violet"
          />

          <StatsCard
            title="Driver Commission"
            value={total_driver_commission}
            icon={<IconTruckDelivery size={24} />}
            color="teal"
          />

          <StatsCard
            title="Total Commission"
            value={total_commission}
            icon={<IconSum size={24} />}
            color="orange"
            isTotal
          />
        </SimpleGrid>

        <Paper withBorder p="md" radius="md">
          <Group position="apart" mb="xs">
            <Text size="xs" weight={700} color="dimmed" uppercase>
              Commission Distribution
            </Text>
            <Text size="xs" color="dimmed">
              {formatNumber(total_commission)} Total
            </Text>
          </Group>
          <Progress
            size="xl"
            radius="xl"
            sections={[
              {
                value: supplierPct,
                color: "blue",
                tooltip: `Supplier: ${supplierPct.toFixed(1)}%`,
              },
              {
                value: driverPct,
                color: "teal",
                tooltip: `Driver: ${driverPct.toFixed(1)}%`,
              },
            ]}
          />
          <Group spacing="xl" mt="sm">
            <BadgeDot
              color="blue"
              label={`Supplier ${supplierPct.toFixed(0)}%`}
            />
            <BadgeDot color="teal" label={`Driver ${driverPct.toFixed(0)}%`} />
          </Group>
        </Paper>
      </Stack>
    </div>
  );
};

const StatsCard = ({ title, value, icon, color, isTotal }) => (
  <Paper
    withBorder
    p="md"
    radius="md"
    sx={(theme) => ({
      backgroundColor: isTotal ? theme.colors.orange[0] : "white",
      borderColor: isTotal ? theme.colors.orange[2] : theme.colors.gray[2],
    })}
  >
    <Group position="apart">
      <div>
        <Text color="dimmed" uppercase weight={700} size="xs">
          {title}
        </Text>
        <Text weight={700} size="xl">
          {formatNumber(value)} ETB
        </Text>
      </div>
      <ThemeIcon color={color} variant="light" size={48} radius="md">
        {icon}
      </ThemeIcon>
    </Group>
  </Paper>
);

const BadgeDot = ({ color, label }) => (
  <Group spacing={5}>
    <Box
      sx={(theme) => ({
        width: 8,
        height: 8,
        borderRadius: 8,
        backgroundColor: theme.colors[color][6],
      })}
    />
    <Text size="xs" color="dimmed" weight={500}>
      {label}
    </Text>
  </Group>
);

const StatsSkeleton = () => (
  <SimpleGrid cols={4} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
    <Skeleton height={90} radius="md" />
    <Skeleton height={90} radius="md" />
    <Skeleton height={90} radius="md" />
    <Skeleton height={90} radius="md" />
  </SimpleGrid>
);

export default CommissionStats;
